import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { linkSocial } from '@/lib/authClient.js';
import type { useNotificationChannels } from '@/lib/notifications.hooks.js';
import type { SlackData } from './notifications.types.js';
import type { SlackSetupStep } from './SlackSetupDialog.js';

interface SlackNotice {
  kind: 'error' | 'success';
  text: string;
}

interface SlackSetupSearch {
  connected?: 'slack' | undefined;
  error?: string | undefined;
}

interface UseSlackSetupFlowOptions {
  search: SlackSetupSearch;
  canManage: boolean;
  isUserPending: boolean;
  clientId: number | undefined;
  slack: SlackData;
  refetchChannels: ReturnType<typeof useNotificationChannels>['refetch'];
}

export function useSlackSetupFlow({
  search,
  canManage,
  isUserPending,
  clientId,
  slack,
  refetchChannels,
}: UseSlackSetupFlowOptions) {
  const navigate = useNavigate();
  const [setupStep, setSetupStep] = useState<SlackSetupStep | null>(null);
  const [configuredChannel, setConfiguredChannel] = useState('');
  const [notice, setNotice] = useState<SlackNotice | null>(null);
  const [successChannel, setSuccessChannel] = useState<string | null>(null);
  const handledCallback = useRef(false);
  const oauthAttempt = useRef(0);
  const setupStepRef = useRef<SlackSetupStep | null>(null);

  function openSetup(step: SlackSetupStep): void {
    setupStepRef.current = step;
    setSetupStep(step);
  }

  function closeSetup(): void {
    oauthAttempt.current += 1;
    setupStepRef.current = null;
    setSetupStep(null);
  }

  useEffect(() => {
    if (search.connected !== 'slack' && search.error === undefined) return;
    if (isUserPending || handledCallback.current) return;
    handledCallback.current = true;

    if (search.error !== undefined) {
      setupStepRef.current = null;
      setSetupStep(null);
      setNotice({ kind: 'error', text: "Slack authorization didn't complete." });
      void navigate({ to: '/settings/notifications', search: {}, replace: true });
      return;
    }

    if (!canManage) {
      setNotice({ kind: 'error', text: 'Only workspace admins can configure Slack.' });
      void navigate({ to: '/settings/notifications', search: {}, replace: true });
      return;
    }

    let active = true;
    void refetchChannels().then((result) => {
      if (!active) return;
      const connectedSlack = result.data?.slack;
      if (
        result.isError ||
        connectedSlack?.status !== 'connected' ||
        connectedSlack.availability !== 'available'
      ) {
        setNotice({
          kind: 'error',
          text: 'Slack connected, but its workspace details could not be loaded. Please retry.',
        });
      } else {
        setConfiguredChannel(connectedSlack.channel);
        setNotice(null);
        setupStepRef.current = 'configure';
        setSetupStep('configure');
      }
      void navigate({ to: '/settings/notifications', search: {}, replace: true });
    });

    return () => {
      active = false;
    };
  }, [canManage, isUserPending, navigate, refetchChannels, search.connected, search.error]);

  async function startSlackLink(): Promise<void> {
    if (!canManage || clientId === undefined) return;
    const attempt = oauthAttempt.current + 1;
    oauthAttempt.current = attempt;
    setNotice(null);
    setSuccessChannel(null);
    openSetup('redirecting');
    const notificationsURL = `${window.location.origin}/settings/notifications`;

    try {
      const result = await linkSocial({
        provider: 'slack',
        callbackURL: `${notificationsURL}?connected=slack`,
        errorCallbackURL: `${notificationsURL}?error=oauth_submit_failed`,
        additionalData: { clientId },
        disableRedirect: true,
      });
      if (oauthAttempt.current !== attempt) return;
      if (result.error) {
        closeSetup();
        setNotice({
          kind: 'error',
          text: result.error.message ?? 'Could not start Slack authorization',
        });
        return;
      }
      const redirectURL = result.data?.url;
      if (!redirectURL) throw new Error('Slack authorization URL is missing');
      window.location.assign(redirectURL);
    } catch {
      if (oauthAttempt.current !== attempt) return;
      closeSetup();
      setNotice({ kind: 'error', text: 'Could not start Slack authorization' });
    }
  }

  function completeChannelConfiguration(channel: string): void {
    if (setupStepRef.current !== 'configure') return;
    setConfiguredChannel(channel);
    openSetup('test');
  }

  function finishSetup(): void {
    closeSetup();
    setNotice(null);
    setSuccessChannel(configuredChannel || slack.channel);
  }

  return {
    setupStep,
    configuredChannel,
    notice,
    successChannel,
    setNotice,
    openSetup,
    closeSetup,
    startSlackLink,
    completeChannelConfiguration,
    finishSetup,
    dismissSuccess: () => setSuccessChannel(null),
  };
}
